import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRequireAuth } from '../lib/auth';
import {
  useApproveUser,
  useDeclineUser,
  useIsAppAdmin,
  usePendingApprovals,
} from '../lib/queries';

export function AdminApprovalsPage() {
  const auth = useRequireAuth();
  const isAdminQ = useIsAppAdmin(auth.user?.id);
  const isAdmin = !!isAdminQ.data;
  const pendingQ = usePendingApprovals(isAdmin);
  const approve = useApproveUser();
  const decline = useDeclineUser();
  const [actingId, setActingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (auth.loading || isAdminQ.isLoading) return <CenterText>Loading…</CenterText>;
  if (!auth.user) return null;

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="eyebrow mb-4">Restricted</div>
        <h1 className="editorial-h1 mb-4">Admin only.</h1>
        <p className="font-editorial italic text-[16px] text-ink-soft mb-8">
          This page is only visible to the system administrator.
        </p>
        <Link to="/dashboard" className="btn-text">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const pending = pendingQ.data ?? [];

  async function onApprove(userId: string) {
    setErr(null);
    setActingId(userId);
    try {
      await approve.mutateAsync(userId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setActingId(null);
    }
  }

  async function onDecline(userId: string) {
    setErr(null);
    setActingId(userId);
    try {
      await decline.mutateAsync(userId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
      <Link
        to="/dashboard"
        className="font-label text-[11px] uppercase tracking-[0.15em] text-ink-muted hover:text-accent transition-colors inline-block mb-6"
      >
        ← dashboard
      </Link>

      <div className="eyebrow mb-4">Admin</div>
      <h1 className="editorial-h1 mb-3">
        Approval <em className="italic text-accent">queue.</em>
      </h1>
      <p className="editorial-deck mb-10 max-w-2xl">
        These users signed up for the CYBHI Onboarding Checklist and confirmed
        their email. Approve to grant access to workspaces and checklists;
        decline to dismiss without granting access.
      </p>

      {err && (
        <div className="border-l-2 border-[#a02525] pl-4 py-2 mb-6 font-editorial italic text-[14px] text-[#a02525]">
          {err}
        </div>
      )}

      {pendingQ.isLoading ? (
        <p className="font-editorial italic text-ink-muted">Loading queue…</p>
      ) : pending.length === 0 ? (
        <div className="border border-rule bg-paper-deep p-7 text-center">
          <p className="font-editorial italic text-[16px] text-ink-soft">
            Nothing pending. You're all caught up.
          </p>
        </div>
      ) : (
        <ul className="space-y-px bg-rule border border-rule">
          {pending.map((p) => {
            const requestedDate = new Date(p.requested_at).toLocaleDateString(
              undefined,
              { year: 'numeric', month: 'long', day: 'numeric' },
            );
            const emailConfirmed = !!p.email_confirmed_at;
            return (
              <li
                key={p.user_id}
                className="bg-paper p-5 flex justify-between items-baseline gap-4 flex-wrap"
              >
                <div className="flex-1 min-w-[200px]">
                  <div className="font-editorial text-[18px] font-medium text-ink leading-snug">
                    {p.full_name ?? '(no name provided)'}
                  </div>
                  <div className="font-editorial text-[14px] text-ink-soft mt-0.5">
                    {p.email}
                  </div>
                  <div className="font-editorial italic text-[12px] text-ink-muted mt-2">
                    Requested {requestedDate}
                    {!emailConfirmed && (
                      <span
                        className="ml-3 font-label not-italic text-[10px] uppercase tracking-[0.18em]"
                        style={{ color: '#a02525' }}
                      >
                        email not yet confirmed
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 items-baseline">
                  <button
                    type="button"
                    onClick={() => onDecline(p.user_id)}
                    disabled={actingId === p.user_id}
                    className="font-label text-[11px] uppercase tracking-[0.15em] text-ink-muted hover:text-[#a02525] transition-colors disabled:opacity-40"
                  >
                    {actingId === p.user_id && decline.isPending ? 'Declining…' : 'Decline'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onApprove(p.user_id)}
                    disabled={actingId === p.user_id}
                    className="btn-text disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {actingId === p.user_id && approve.isPending ? 'Approving…' : 'Approve →'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CenterText({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      {children}
    </div>
  );
}
